import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * 1. CORE FETCH USER FUNCTION
   * Fetches profile and updates state.
   */
  const fetchMe = useCallback(async () => {
    // If no token exists in storage, don't even try (prevents unnecessary 401s)
    const hasToken = localStorage.getItem("access_token");
    if (!hasToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("access_token");
        sessionStorage.setItem("auth_notice", "session_expired");
        toast.error("Session expired. Please log in again.");
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);


  /**
   * 2. SOCIAL LOGIN SYNC HELPER
   * Used by Login.jsx when a token arrives in the URL
   */
  const setTokenAndFetchUser = useCallback(async (token) => {
    setLoading(true);
    localStorage.setItem("access_token", token);
    await fetchMe();
  }, [fetchMe]);

  /**
   * 3. INITIALIZATION
   * One effect to rule them all.
   */
  useEffect(() => {
    const initAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get("token");

      if (urlToken) {
        localStorage.setItem("access_token", urlToken);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      await fetchMe();
    };

    initAuth();
  }, [fetchMe]);

  /**
   * 4. LOGIN
   */
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      
      // Save token if backend returns one
      if (data.access_token) {
        localStorage.setItem("access_token", data.access_token);
      }
      
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatApiError(e.response?.data?.detail) || e.message,
      };
    }
  };

  /**
   * 5. REGISTER
   */
  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      
      if (data.access_token) {
        localStorage.setItem("access_token", data.access_token);
      }
      
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatApiError(e.response?.data?.detail) || e.message,
      };
    }
  };

  /**
   * 6. LOGOUT
   */
  const logout = async () => {
    try {
      // Best effort logout on backend
      await api.post("/auth/logout").catch(() => {});
    } finally {
      localStorage.removeItem("access_token");
      setUser(null);
      window.location.assign("/login");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        setTokenAndFetchUser, // Exported for Social Login
        refresh: fetchMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
